import api from '@/api/api.ts';
import { useApiGet } from '@/api/apiRequests.ts';
import { getOrps } from '@/api/services';
import { getFilters } from '@/api/services/getFilters.ts';
import { getMetrics } from '@/api/services/getMetrics.ts';
import { PageLoader } from '@/components/PageLoader.tsx';
import { getChartOptions, prepareChartData } from '@/utils/chartUtils.ts';
import { AxiosError } from 'axios';
import { Button } from 'primereact/button';
import { Chart } from 'primereact/chart';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { FC, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

interface PredictorItem {
  label: string;
  value: string;
}

interface PredictionData {
  [city: string]: {
    [year: string]: {
      [metric: string]: {
        value: number;
        // biome-ignore lint/style/useNamingConvention: <explanation>
        is_predicted: boolean;
        // biome-ignore lint/style/useNamingConvention: <explanation>
        lower_bound: number | null;
        // biome-ignore lint/style/useNamingConvention: <explanation>
        upper_bound: number | null;
      };
    };
  };
}

export const Predicts: FC = () => {
  const [predictorData, setPredictorData] = useState<PredictorItem[]>([]);
  const [predictor, setPredictor] = useState('');
  const [confidence, setConfidence] = useState<number | null>(0.95);
  const [years, setYears] = useState<number | null>(1);
  const [metricsData, setMetricsData] = useState();
  const [orpsData, setOrpsData] = useState();
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [selectedOrps, setSelectedOrps] = useState<string[]>([]);
  const [predictionResults, setPredictionResults] = useState<PredictionData | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const { data } = useApiGet<any, AxiosError>(['orps'], async () => await getOrps(), {
    enabled: true,
  });
  const { data: metrics } = useApiGet<any, AxiosError>(['metrics'], async () => await getMetrics(), {
    enabled: true,
  });
  const { data: filterData, isLoading } = useApiGet<any, AxiosError>(['filters'], async () => await getFilters(), {
    enabled: true,
  });

  useEffect(() => {
    if (filterData?.predictor) {
      const formattedData = Object.entries(filterData.predictor).map(([value, label]) => ({
        label: String(label),
        value,
      }));
      setPredictorData(formattedData);
    }
    setMetricsData(metrics);
    // Transform ORPs for MultiSelect
    const transformedOrps = data?.map((orp: any) => ({
      ...orp,
      label: orp.municipality?.name || orp.municipality?.orp_name || '',
    })) || [];
    setOrpsData(transformedOrps);
  }, [filterData, metrics, data]);

  const generatePredicts = async () => {
    try {
      setIsGenerating(true);
      let orpsArray: string[] = [];
      let metricsArray: string[] = [];

      if (Array.isArray(selectedOrps) && selectedOrps.length > 0) {
        orpsArray = selectedOrps.map((item: any) => item.label || item.municipality?.name || item.municipality?.orp_name || '');
      }

      if (Array.isArray(selectedMetrics) && selectedMetrics.length > 0) {
        metricsArray = selectedMetrics.map((item: any) => item.metric_code);
      }

      const queryParams = new URLSearchParams({
        confidence: confidence?.toString() || '0.95',
        predictor: predictor,
        // biome-ignore lint/style/useNamingConvention: <explanation>
        n_years: years?.toString() || '1',
      });

      const response = await api.post(`/orp-trends?${queryParams.toString()}`, {
        orps: orpsArray,
        metrics: metricsArray,
      });

      if (response.status === 200 || response.status === 201 || response.status === 202) {
        setPredictionResults(response.data);
      }
    } catch (error: any) {
      if (error?.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Error while generating predictions.');
      }
      console.error('Error generating predictions:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const isFormValid = () => {
    return (
      predictor &&
      predictor.length > 0 &&
      confidence !== null &&
      years !== null &&
      selectedOrps.length > 0 &&
      selectedMetrics.length > 0
    );
  };

  const renderCharts = () => {
    if (!predictionResults) return null;

    // Get all metrics from the first city (assuming all cities have the same metrics)
    const firstCity = Object.keys(predictionResults)[0];
    if (!firstCity) return null;

    const firstYear = Object.keys(predictionResults[firstCity])[0];
    if (!firstYear) return null;

    const metrics = Object.keys(predictionResults[firstCity][firstYear]);
    const chartOptions = getChartOptions();

    return metrics.map((metric) => {
      const chartData = prepareChartData(metric, predictionResults);
      if (!chartData) return null;

      return (
        <div key={metric} className="mt-8 p-4 bg-white rounded-lg shadow">
          <h3 className="text-xl font-bold mb-4">{metric}</h3>
          <div>
            <Chart type="line" data={chartData} options={chartOptions} />
          </div>
        </div>
      );
    });
  };

  const chartContent = useMemo(() => {
    if (!predictionResults) return null;
    return renderCharts();
  }, [predictionResults, JSON.stringify(predictionResults)]);

  return (
    <>
      <h1 className={'text-2xl font-bold mb-8 text-white'}>Predictions</h1>
      <div className={'flex flex-col justify-center items-center gap-4'}>
        <div className={'flex flex-wrap lg:flex-nowrap gap-4 w-full md:max-w-2/3'}>
          <div className={'flex flex-col gap-1 w-full'}>
            <label htmlFor={'confidence'} className={'font-bold'}>
              Confidence
            </label>
            <InputNumber
              value={confidence}
              id={'confidence'}
              maxFractionDigits={2}
              onChange={(e) => setConfidence(e.value)}
              onBlur={(e) => {
                const value = parseFloat(e.target.value);
                if (value < 0.05) setConfidence(0.05);
                if (value > 0.99) setConfidence(0.99);
              }}
              useGrouping={false}
            />
          </div>
          <div className={'flex flex-col gap-1 w-full'}>
            <label htmlFor={'predictor'} className={'font-bold'}>
              Predictor
            </label>
            <Dropdown
              value={predictor}
              options={predictorData}
              loading={isLoading}
              id={'predictor'}
              optionLabel={'label'}
              placeholder={'Choose predictor'}
              onChange={(e: DropdownChangeEvent) => setPredictor(e.value)}
            />
          </div>
        </div>
        <div className={'flex flex-wrap lg:flex-nowrap gap-4 w-full md:max-w-2/3'}>
          <div className={'flex flex-col gap-1 w-full'}>
            <label htmlFor={'years'} className={'font-bold'}>
              Years
            </label>
            <InputNumber
              value={years}
              showButtons={true}
              id={'years'}
              step={1}
              maxFractionDigits={0}
              onChange={(e) => setYears(e.value)}
              onBlur={(e) => {
                const value = parseFloat(e.target.value);
                if (value < 1) setYears(1);
                if (value > 50) setYears(50);
              }}
              useGrouping={false}
            />
          </div>
          <div className={'flex flex-col gap-1 w-full'}>
            <label htmlFor={'cities'} className={'font-bold'}>
              Cities
            </label>
            <MultiSelect
              value={selectedOrps}
              onChange={(e: MultiSelectChangeEvent) => setSelectedOrps(e.value)}
              options={orpsData}
              optionLabel="label"
              id={'cities'}
              filter={true}
              placeholder="Choose cities"
              maxSelectedLabels={7}
            />
          </div>
        </div>
        <div className={'flex flex-col gap-1 w-full mb-4 md:max-w-2/3'}>
          <label htmlFor={'metrics'} className={'font-bold'}>
            Metrics
          </label>
          <MultiSelect
            value={selectedMetrics}
            onChange={(e: MultiSelectChangeEvent) => setSelectedMetrics(e.value)}
            options={metricsData}
            optionLabel="metric_code"
            id={'metrics'}
            filter={true}
            placeholder="Choose metrics"
            maxSelectedLabels={7}
          />
        </div>
        <Button className={'btn-primary'} style={{ borderRadius: '6px' }} disabled={!isFormValid()} onClick={generatePredicts} loading={isGenerating}>
          Generate predictions
        </Button>
      </div>

      {/* Render charts if we have prediction results */}
      {predictionResults && <div className="mt-8 w-full">{chartContent}</div>}

      <PageLoader visible={isLoading} />
    </>
  );
};
